import type { SingleAssetValueOverview } from '@einwert/valuation-api-client'
import {
    assembleAssetTitle,
    AssetList,
    EmptyState,
    ListCard,
    ListItem,
    PageGridContainer,
    PageGridItem,
    tokens,
    ContentChip,
} from 'ComponentLibrary'
import { useAssetListPageData } from 'ClientPortal/src/pages/Assets/AssetList/AssetListPage.data'
import {
    Accordion as MuiAccordion,
    AccordionDetails as MuiAccordionDetails,
    AccordionSummary as MuiAccordionSummary,
    Stack,
    styled,
    useTheme,
} from '@mui/material'
import { Link } from 'react-router-dom'
import { ValuationInfoCell } from 'ClientPortal/src/pages/Assets/AssetList/AssetCardCell/ValuationInfoCell'
import { AddressInfoCell } from 'ClientPortal/src/pages/Assets/AssetList/AssetCardCell/AddressInfoCell'
import { OrderInfoCell } from 'ClientPortal/src/pages/Assets/AssetList/AssetCardCell/OrderInfoCell'
import { MarketValueInfoCell } from 'ClientPortal/src/pages/Assets/AssetList/AssetCardCell/MarketValueInfoCell'
import {
    BuildingOfficeIcon,
    CalendarDotIcon,
    CaretDownIcon,
    DownloadSimpleIcon,
    ListChecksIcon,
    MicrosoftExcelLogoIcon,
} from '@phosphor-icons/react'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { AssetListLoadingPage } from 'ClientPortal/src/pages/Assets/AssetList/AssetListPage.load'
import { isEmpty } from 'lodash'
import { useState } from 'react'
import { ExportMissingDataModal } from 'ClientPortal/src/pages/Assets/AssetList/ExportMissingData/ExportMissingDataModal'

import Typography from 'ComponentLibrary/src/piazza/text/Typography/Typography'
import { ValXmlDownloadModal } from 'ClientPortal/src/common/modals/ValXmlDownloadModal'
import { useValXmlBulkExportUrl } from 'ClientPortal/src/api/Queries/files'
import { downloadFileFromLink } from 'ClientPortal/src/common/utils/files'
import { ExportActionButton } from 'ComponentLibrary/src/piazza/buttons/ExportActionButton/ExportActionButton'
import { useGetCurrentUser } from 'ClientPortal/src/api/Queries/users'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

export function AssetListPage() {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tExport } = useTranslation('terminology', { keyPrefix: 'export' })
    const { t: tAssetListPage } = useTranslation('pages', { keyPrefix: 'assets.assetListPage' })
    const locale = useCurrentResolvedLocale()
    const theme = useTheme()
    const [isExportModalOpen, setIsExportModalOpen] = useState(false)
    const [isValXMLModalOpen, setIsValXMLModalOpen] = useState(false)
    const { user: currentUser } = useGetCurrentUser()
    const { generateValXmlBulkExportUrl, isGeneratingValXmlBulkExportUrl } = useValXmlBulkExportUrl({ onSuccess: downloadFileFromLink })
    const { isMultiPortfolioValueOverviewLoading, getAssetsPerPortfolio } = useAssetListPageData()
    const assetsPerPortfolio = getAssetsPerPortfolio()
    const hasAssetsWithoutPortfolioOnly = assetsPerPortfolio.length === 1 && assetsPerPortfolio[0].type === 'synthetic'
    const isAppraiser = currentUser?.role === 'appraiser'

    const sortedPortfolios = [...assetsPerPortfolio].sort((portfolioA, portfolioB) => {
        const isSyntheticA = portfolioA.type === 'synthetic'
        const isSyntheticB = portfolioB.type === 'synthetic'

        if (isSyntheticA && !isSyntheticB) {
            return 1
        }
        if (!isSyntheticA && isSyntheticB) {
            return -1
        }

        return portfolioA.title.localeCompare(portfolioB.title)
    })

    const sortAssetsAlphabetically = (assets: SingleAssetValueOverview[]) =>
        [...assets].sort((assetA, assetB) => {
            const titleA = assembleAssetTitle({ ...assetA.address, customer_asset_reference: assetA.customer_asset_reference })
            const titleB = assembleAssetTitle({ ...assetB.address, customer_asset_reference: assetB.customer_asset_reference })
            return titleA.localeCompare(titleB, 'de', { sensitivity: 'base' })
        })

    if (isMultiPortfolioValueOverviewLoading) {
        return <AssetListLoadingPage />
    }

    const ExpandIcon = <CaretDownIcon size={tokens.SizingIconM} color={theme.palette.customtext.darkblue100} />

    const formatClosingDate = (closing_date: { day: number; month: number }) => {
        const closingDate = new Date(2000, closing_date.month - 1, closing_date.day)
        const formattedClosingDate = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' }).format(closingDate)
        return tAssetListPage('closingDateLabel', { date: formattedClosingDate })
    }

    return (
        <PageGridContainer withBottomSpace withTopSpace withFooter>
            <PageGridItem columnSpan={6} columnSpanXs={4}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Headline title={tCommon('assets')} variant="headline1" />
                    {!isEmpty(sortedPortfolios) ? (
                        <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                            <Typography variant="textS" color={tokens.ColorTextDefault}>
                                {tExport('sectionTitle')}
                            </Typography>
                            {isAppraiser && (
                                <ExportActionButton
                                    leftIcon={<ListChecksIcon />}
                                    rightIcon={<DownloadSimpleIcon />}
                                    label={tExport('missingData.featureLabel')}
                                    onClick={() => setIsExportModalOpen(true)}
                                />
                            )}
                            <ExportActionButton
                                leftIcon={<MicrosoftExcelLogoIcon />}
                                rightIcon={<DownloadSimpleIcon />}
                                label={tCommon('allValXmls')}
                                onClick={() => setIsValXMLModalOpen(true)}
                            />
                        </Stack>
                    ) : null}
                </Stack>
            </PageGridItem>
            {isEmpty(assetsPerPortfolio) && (
                <PageGridItem columnSpan={6} columnSpanXs={4}>
                    <EmptyState style={{ marginTop: '12vh' }} size="enormous" title={tAssetListPage('emptyStateTitle')} />
                </PageGridItem>
            )}
            {sortedPortfolios.map(({ title: portfolioTitle, assets, closing_date, uuid: portfolioId, type }) => {
                const sortedAssets = sortAssetsAlphabetically(assets)

                const portfolioContent = (
                    <PageGridItem columnSpan={6} columnSpanXs={4}>
                        <AssetList aria-label="assetList">
                            {sortedAssets.map(
                                ({
                                    address,
                                    uuid: assetId,
                                    title_image,
                                    title_thumbnail_image,
                                    valuation,
                                    rent,
                                    customer_asset_reference,
                                }) => (
                                    <ListItemWithSvgTransition key={assetId}>
                                        <Link to={`/assets/${assetId}`} style={{ width: '100%' }}>
                                            <ListCard imageSrc={title_thumbnail_image} imageFallbackSrc={title_image}>
                                                <AddressInfoCell address={address} customer_asset_reference={customer_asset_reference} />
                                                <OrderInfoCell valuation={valuation} />
                                                <ValuationInfoCell valuation={valuation} rent={rent} />
                                                <MarketValueInfoCell valuation={valuation} />
                                            </ListCard>
                                        </Link>
                                    </ListItemWithSvgTransition>
                                )
                            )}
                        </AssetList>
                    </PageGridItem>
                )

                return hasAssetsWithoutPortfolioOnly ? (
                    portfolioContent
                ) : (
                    <PageGridItem columnSpan={6} columnSpanXs={4}>
                        <Accordion key={portfolioId} disableGutters elevation={0} defaultExpanded={true}>
                            <AccordionSummary expandIcon={ExpandIcon}>
                                <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing075}>
                                    <Typography variant="headline3" color={tokens.ColorTextBoldest}>
                                        {type === 'synthetic' ? tAssetListPage('syntheticPortfolioTitle') : portfolioTitle}
                                    </Typography>
                                    <ContentChip size="small" color="grey" label={assets.length} icon={<BuildingOfficeIcon />} />
                                    {closing_date && (
                                        <ContentChip
                                            size="small"
                                            color="grey"
                                            label={formatClosingDate(closing_date)}
                                            icon={<CalendarDotIcon />}
                                        />
                                    )}
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails>{portfolioContent}</AccordionDetails>
                        </Accordion>
                    </PageGridItem>
                )
            })}
            {isAppraiser && isExportModalOpen && <ExportMissingDataModal isOpen close={() => setIsExportModalOpen(false)} />}
            <ValXmlDownloadModal
                isOpen={isValXMLModalOpen}
                onClose={() => setIsValXMLModalOpen(false)}
                onDownload={async () => {
                    generateValXmlBulkExportUrl()
                    setIsValXMLModalOpen(false)
                }}
                isDownloading={isGeneratingValXmlBulkExportUrl}
                mode="multiple"
            />
        </PageGridContainer>
    )
}

const AccordionSummary = styled(MuiAccordionSummary)`
    padding-left: 0;
`

const AccordionDetails = styled(MuiAccordionDetails)`
    padding-left: 0;
`

const Accordion = styled(MuiAccordion)`
    margin-bottom: ${({ theme }) => theme.spacing(4)};
`

const ListItemWithSvgTransition = styled(ListItem)(() => ({
    '& svg': {
        opacity: '0',
        transition: 'opacity 0.2s ease-out',
    },
    '&:hover, &:focus': {
        '& svg': {
            opacity: '1',
            transition: 'opacity 0.2s ease-in',
        },
    },
}))
